import { Directory, Filesystem } from '@capacitor/filesystem'

import { resolveToCapacitorUri } from './exercise-media-cache'
import { isNative } from './platform'
import type { TopicSummary } from './lingodb'

/**
 * Persistent thumbnail cache for the topic library.
 *
 * On native (iOS / Android), thumbnail bytes go to `Directory.Data` —
 * private app storage that survives restarts but disappears on uninstall.
 * The JSON index (`lingodb/topics.json`) holds the topic metadata plus a
 * pointer to the locally-cached file. When the picker screen renders, the
 * `<img>` tag points at the converted capacitor:// URI and the WebView
 * loads it instantly with zero network.
 *
 * On web, every helper is a no-op or returns null — therapists run in a
 * browser without the Filesystem plugin and have no offline requirement.
 *
 * Layout under Directory.Data:
 *   lingodb/topics.json                  — JSON index, see StoredTopic[]
 *   lingodb/thumbnails/topic-<id>.bin    — raw thumbnail bytes
 *
 * The index stores each thumbnail as a RELATIVE path and resolves it to a
 * live `capacitor://` URI on load — the rule `exercise-media-cache.ts` already
 * follows, for the same reason. An absolute URI embeds the iOS App Container
 * UUID, which rotates on every app update: the bytes survive, but a stored
 * URI points at the old container and every topic cover goes blank. It never
 * healed on its own either, because a re-download only happens when the URI
 * is null or the topic changed upstream. Indexes written that way before the
 * fix are repaired in {@link loadCachedTopics}.
 */

const INDEX_FILE = 'lingodb/topics.json'
const THUMB_DIR = 'lingodb/thumbnails'

/**
 * The @capacitor/filesystem Android implementation ignores `recursive: true`
 * on `downloadFile` (see LegacyFilesystemImplementation.kt:103-120 — it goes
 * straight to FileOutputStream without ever mkdir'ing the parent), so every
 * thumbnail download fails with ENOENT on first launch when the lingodb/
 * subtree doesn't exist yet. iOS auto-creates parents either way. We mkdir
 * once per session before the first download to side-step the plugin bug.
 */
const ensuredDirs = new Set<string>()
async function ensureCacheDir(path: string): Promise<void> {
  if (!isNative || ensuredDirs.has(path)) return
  try {
    await Filesystem.mkdir({ path, directory: Directory.Data, recursive: true })
  } catch {
    // Already exists — fine. The plugin rejects on exists even with recursive.
  }
  ensuredDirs.add(path)
}

/**
 * A topic as written to the index: the relative path of its thumbnail rather
 * than a URI. `thumbnail_local_uri` appears only in indexes written before
 * the relative-path fix, and is never written again.
 */
type StoredTopic = Omit<CachedTopic, 'thumbnail_local_uri'> & {
  thumbnail_path?: string | null
  thumbnail_local_uri?: string | null
}

export interface CachedTopic extends TopicSummary {
  /** The Capacitor-converted URI of the locally-cached thumbnail file, or
   *  null if we haven't downloaded one yet (e.g. topic has no cover image
   *  upstream, or the download failed last time). */
  thumbnail_local_uri: string | null
  /** Wall-clock timestamp of the last successful thumbnail write. We use
   *  it to skip re-downloading when the topic's `updated_at` hasn't moved. */
  thumbnail_cached_at: string | null
}

// --- Index file ----------------------------------------------------------

/**
 * Read the cached topic list. Returns [] on web, on first launch, or when
 * the file is missing/corrupt — never throws.
 */
export async function loadCachedTopics(): Promise<CachedTopic[]> {
  if (!isNative) return []
  try {
    const { data } = await Filesystem.readFile({
      path: INDEX_FILE,
      directory: Directory.Data,
      encoding: 'utf8' as never,
    })
    const text = typeof data === 'string' ? data : await (data as Blob).text()
    const parsed = JSON.parse(text)
    if (!Array.isArray(parsed)) return []
    return await Promise.all((parsed as StoredTopic[]).map(hydrate))
  } catch {
    // No cache yet, or it got wiped — neither is an error worth bubbling.
    return []
  }
}

/** One stored topic → its in-memory form, with a URI valid for this run. */
async function hydrate(stored: StoredTopic): Promise<CachedTopic> {
  const { thumbnail_path, thumbnail_local_uri: legacyUri, ...topic } = stored

  let path: string | null
  if (thumbnail_path !== undefined) {
    path = thumbnail_path
  } else if (legacyUri) {
    // Written before the fix: a baked URI into a container that may no longer
    // exist. The file itself always lived at the topic's fixed path, so keep
    // it when it is still there — no download — and drop it when it is not,
    // which is what makes the next refresh fetch it again. Only a device that
    // has actually lost the file pays for a download.
    const candidate = thumbnailPath(topic.id)
    path = (await fileExists(candidate)) ? candidate : null
  } else {
    path = null
  }

  return {
    ...topic,
    thumbnail_local_uri: path === null ? null : await resolveToCapacitorUri(path),
    thumbnail_cached_at: path === null ? null : topic.thumbnail_cached_at,
  }
}

async function fileExists(path: string): Promise<boolean> {
  try {
    await Filesystem.stat({ path, directory: Directory.Data })
    return true
  } catch {
    return false
  }
}

export async function saveCachedTopics(topics: CachedTopic[]): Promise<void> {
  if (!isNative) return
  // Paths, never the live URIs — see the header.
  const stored: StoredTopic[] = topics.map(({ thumbnail_local_uri, ...topic }) => ({
    ...topic,
    thumbnail_path: thumbnail_local_uri === null ? null : thumbnailPath(topic.id),
  }))
  await Filesystem.writeFile({
    path: INDEX_FILE,
    data: JSON.stringify(stored),
    directory: Directory.Data,
    encoding: 'utf8' as never,
    recursive: true,
  })
}

// --- Thumbnails ----------------------------------------------------------

/**
 * Fetch the presigned URL and write the bytes to the cache. Returns the
 * Capacitor-converted URI that `<img src>` can render directly.
 *
 * We use Filesystem.downloadFile() rather than `fetch()` + writeFile so the
 * native HTTP stack (URLSession on iOS, OkHttp on Android) handles the
 * download. That stack is not subject to the WebView's CORS rules — the
 * Hetzner S3 bucket doesn't allowlist `capacitor://localhost`, so a fetch
 * from the WebView fails preflight while a native download succeeds.
 * Bonus: bytes stream straight to disk, skipping the blob→base64 round-trip.
 *
 * Throws on download failure — the caller decides whether to leave the
 * existing cached thumbnail in place or drop the topic to "no cover".
 */
export async function downloadThumbnail(topicId: number, url: string): Promise<string> {
  if (!isNative) {
    // On web we don't cache to disk — caller should render the presigned URL.
    return url
  }

  const path = thumbnailPath(topicId)
  await ensureCacheDir(THUMB_DIR)
  await Filesystem.downloadFile({
    url,
    path,
    directory: Directory.Data,
    recursive: true,
  })

  // Resolved from the relative path rather than the absolute one the plugin
  // returns, so this run and every later one agree on what the file is called.
  return resolveToCapacitorUri(path)
}

/**
 * Delete any thumbnail files in the cache directory whose topic id isn't
 * in `keep`. Tolerates a missing directory (first launch, post-reset).
 */
export async function pruneOrphans(keep: number[]): Promise<void> {
  if (!isNative) return
  const keepSet = new Set(keep.map(String))
  try {
    const { files } = await Filesystem.readdir({ path: THUMB_DIR, directory: Directory.Data })
    for (const entry of files) {
      const name = entry.name
      const match = /^topic-(\d+)\./.exec(name)
      if (!match) continue
      if (!keepSet.has(match[1])) {
        await Filesystem.deleteFile({
          path: `${THUMB_DIR}/${name}`,
          directory: Directory.Data,
        }).catch(() => {
          // Race with another writer is harmless — fall through.
        })
      }
    }
  } catch {
    // Directory doesn't exist yet → nothing to prune.
  }
}

// --- internals -----------------------------------------------------------

function thumbnailPath(topicId: number): string {
  // The file extension doesn't matter for rendering once it's behind a
  // capacitor:// URI — the WebView sniffs the content type. We use ".bin"
  // to make that explicit.
  return `${THUMB_DIR}/topic-${topicId}.bin`
}
